import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// import Navbar from "./NavBar";
import axios from "axios";
import Announcementslider from "./Announcementslider.jsx";
import CustomNavbar from "./CustomPermissions/CustomNavbar.jsx";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../config/api";
import { exportRecordsToExcel } from "../utils/exportUtils";

function PrevMonthRecords() {
  const [data, setData] = useState(null);
  const [dailyEntryData, setDailyEntryData] = useState([]);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [pipelineandturnedup, setPipelineandturnedup] = useState([]);
  const [candidateRecords, setCandidateRecords] = useState({});
  const [pipelineRecords, setPipelineRecords] = useState({});
  const [activeFilter, setActiveFilter] = useState({});
  const [uniquePipelineCounts, setUniquePipelineCounts] = useState({});
  const [expandedRows, setExpandedRows] = useState(null);
  const [nestedExpandedRows, setNestedExpandedRows] = useState({});
  const [displayMode, setDisplayMode] = useState('target_candidates'); // 'target_candidates' or 'revenue'
  const navigate = useNavigate();

  // Fetch display mode settings
  useEffect(() => {
    const fetchDisplayMode = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/display-mode-settings/'),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDisplayMode(response.data.pace_display_mode || 'target_candidates');
        }
      } catch (error) {
        console.error("Error fetching display mode:", error);
      }
    };
    fetchDisplayMode();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl("/GetPreviousMonthRecords/"),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const {
            records,
            monthly_records,
            is_superuser,
            user_permissions,
            pipelineandturnedup,
          } = response.data;

          setData(records || []);
          setDailyEntryData(monthly_records || []);
          setIsSuperuser(is_superuser);
          setPipelineandturnedup(pipelineandturnedup || []);

          // Set unique pipeline counts from backend
          const uniquePipelineCountsMap = {};
          monthly_records.forEach((entry) => {
            uniquePipelineCountsMap[entry.user] = entry.pipeline_unique || 0;
          });
          setUniquePipelineCounts(uniquePipelineCountsMap);

          if (is_superuser) {
            // Superuser logic
            localStorage.setItem("is_superuser", is_superuser);
          } else if (user_permissions) {
            localStorage.setItem("is_superuser", false);
            localStorage.setItem(
              "custom_permissions",
              JSON.stringify(user_permissions)
            );
          }
        }
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
      }
    };

    fetchData();
  }, [navigate]);

  // Function to fetch candidate records for previous month
  const fetchCandidateRecords = async (userId, filterType) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      // Calculate previous month
      const today = new Date();
      const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
      const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

      const response = await axios.get(
        getApiUrl(`/GetAllCandidateOfUser/${userId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            status: filterType,
            month: prevMonth,
            year: prevYear
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching candidate records:", error);
      return [];
    }
  };

  // Toggle expanded row
  const toggleExpandedRow = async (userId, filterType) => {
    if (expandedRows === userId && activeFilter[userId] === filterType) {
      setExpandedRows(null);
      setActiveFilter((prev) => ({ ...prev, [userId]: null }));
    } else {
      const records = await fetchCandidateRecords(userId, filterType);
      setCandidateRecords((prev) => ({ ...prev, [userId]: records }));
      setExpandedRows(userId);
      setActiveFilter((prev) => ({ ...prev, [userId]: filterType }));
    }
  };

  // Toggle nested expanded row
  const toggleNestedExpandedRow = (userId, recordId) => {
    setNestedExpandedRows((prev) => {
      const updatedNestedRows = { ...prev };
      if (updatedNestedRows[userId] === recordId) {
        delete updatedNestedRows[userId];
      } else {
        updatedNestedRows[userId] = recordId;
      }
      return updatedNestedRows;
    });
  };

  // Handle display mode change
  const handleDisplayModeChange = async (newMode) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.put(
        getApiUrl('/display-mode-settings/update/'),
        {
          pace_display_mode: newMode
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );

      setDisplayMode(newMode);
      alert(response.data.message || 'Display mode updated successfully!');
    } catch (error) {
      console.error("Error updating display mode:", error);
      alert(error.response?.data?.message || "Error updating display mode. Please try again.");
    }
  };

  const sortedData = useMemo(() => {
    if (!dailyEntryData || !pipelineandturnedup || !data) return [];

    return dailyEntryData
      .map((entry) => {
        const pipeline =
          pipelineandturnedup.find((item) => item.user === entry.user)
            ?.pipline || 0;

        const turnedUpRatio =
          entry.turned_up && pipeline
            ? ((entry.selected + entry.turned_up) / entry.resume_forwarded) *
            100
            : 0;

        // Get target revenue from data
        const correspondingRecord = data.find(
          (record) => record.id === entry.user
        );
        const targetRev = correspondingRecord ? correspondingRecord.revenue : 0;
        const target = correspondingRecord ? correspondingRecord.target : 0;

        return {
          ...entry,
          pipeline,
          turnedUpRatio,
          targetRev, // Add target revenue for sorting
          target,
        };
      })
      .sort((a, b) => {
        // Sort based on display mode
        const bExp = isNaN(b.expected_revenue) ? 0 : parseFloat(b.expected_revenue);
        const aExp = isNaN(a.expected_revenue) ? 0 : parseFloat(a.expected_revenue);
        const bRev = isNaN(b.revenue) ? 0 : parseFloat(b.revenue);
        const aRev = isNaN(a.revenue) ? 0 : parseFloat(a.revenue);

        if (displayMode === 'revenue') {
          // Sort by expected revenue first when in revenue mode
          if (Math.abs(bExp - aExp) > 0.01) return bExp - aExp;
          if (Math.abs(bRev - aRev) > 0.01) return bRev - aRev;
          if (b.selected !== a.selected) return b.selected - a.selected;
          if (b.resume_forwarded !== a.resume_forwarded)
            return b.resume_forwarded - a.resume_forwarded;
          if (b.turnedUpRatio !== a.turnedUpRatio)
            return b.turnedUpRatio - a.turnedUpRatio;
          return b.pipeline - a.pipeline;
        } else {
          // Sort by target/candidates (selected) first when in target mode
          if (b.selected !== a.selected) return b.selected - a.selected;
          if (Math.abs(bExp - aExp) > 0.01) return bExp - aExp;
          if (Math.abs(bRev - aRev) > 0.01) return bRev - aRev;
          if (b.resume_forwarded !== a.resume_forwarded)
            return b.resume_forwarded - a.resume_forwarded;
          if (b.turnedUpRatio !== a.turnedUpRatio)
            return b.turnedUpRatio - a.turnedUpRatio;
          return b.pipeline - a.pipeline;
        }
      });
  }, [dailyEntryData, pipelineandturnedup, data, displayMode]);

  // Calculate the totals for each column
  const totalForwarded = sortedData.reduce(
    (acc, curr) => acc + curr.resume_forwarded,
    0
  );
  const totalPipeline = sortedData.reduce(
    (acc, curr) => acc + curr.pipeline,
    0
  );
  const totalTurnedUp = sortedData.reduce(
    (acc, curr) => acc + curr.turned_up,
    0
  );
  const totalSelected = sortedData.reduce(
    (acc, curr) => acc + curr.selected,
    0
  );
  const totalJoined = sortedData.reduce(
    (acc, curr) => acc + (curr.joined || 0),
    0
  );
  const totalBackout = sortedData.reduce(
    (acc, curr) => acc + (curr.backout || 0),
    0
  );
  const totalRevenue = sortedData.reduce(
    (acc, curr) => acc + (isNaN(curr.revenue) ? 0 : parseFloat(curr.revenue)),
    0
  );
  const totalTargetRevenue = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.revenue || 0);
  }, 0);
  const totalTargetCandidates = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.target || 0);
  }, 0);

  const totalPercentage = displayMode === 'target_candidates'
    ? (totalTargetCandidates > 0 ? (totalSelected / totalTargetCandidates) * 100 : 0)
    : (totalTargetRevenue > 0 ? (totalRevenue / totalTargetRevenue) * 100 : 0);

  return (
    <>
      {/* <Navbar /> */}
      <section style={{ maxHeight: "100vh" }}>
        <div style={{ display: "flex" }}>

          <>
            <CustomNavbar />

            <div
              style={{
                // height: "100vh",
                width: "99vw",
                // overflowY: "scroll",
                border: "1px solid #ddd",
                // padding: "10px",
                padding: "0 10px 10px 10px",
              }}
            >
              <div className="flex justify-between items-center mb-4" style={{ marginLeft: "30px", marginRight: "30px", marginTop: "10px" }}>
                <h2 className="text-blue-500 text-2xl font-bold">
                  HR Associate Previous Month's Records
                </h2>
                <div className="flex gap-4 items-center">
                  {isSuperuser && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Display Mode:</label>
                      <select
                        value={displayMode}
                        onChange={(e) => handleDisplayModeChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="target_candidates">Target & Candidates Closed</option>
                        <option value="revenue">Revenue Target & Revenue Generated</option>
                      </select>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      const exportData = sortedData.map((entry) => {
                        const user = data.find((r) => r.id === entry.user);
                        return {
                          name: user?.name || "",
                          username: user?.username || "",
                          resume_forwarded: entry.resume_forwarded || 0,
                          pipeline_unique: uniquePipelineCounts[entry.user] || 0,
                          pipeline: entry.pipeline || 0,
                          turned_up: entry.turned_up || 0,
                          selected: entry.selected || 0,
                          joined: entry.joined || 0,
                          backout: entry.backout || 0,
                          revenue: entry.revenue || 0,
                          target: user?.target || 0,
                        };
                      });
                      exportRecordsToExcel(exportData, `HR_PrevMonth_Records_${new Date().toISOString().split('T')[0]}`);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    📥 Export to Excel
                  </button>
                </div>
              </div>
              <table
                style={{
                  width: "100%",
                  // borderCollapse: "collapse",
                  backgroundColor: "white",
                }}
              >
                <thead>
                  <tr
                    className="bg-blue-500"
                    style={{
                      background: "",
                      position: "sticky",
                      top: "0",
                      padding: 0,
                      marginTop: 0,
                      border: "1px solid transparent",
                      borderRadius: "35px",
                      fontSize: "16px",
                    }}
                  >
                    <th className="py-2 px-4 border-b">Images</th>
                    <th className="py-2 px-4 border-b">Name</th>
                    <th className="py-2 px-4 border-b">Forwarded</th>
                    <th className="py-2 px-4 border-b">Pipeline</th>
                    <th className="py-2 px-4 border-b">Turned Up</th>
                    <th className="py-2 px-4 border-b">
                      Selected (Achieved / Target)
                    </th>
                    <th className="py-2 px-4 border-b">Joined</th>
                    <th className="py-2 px-4 border-b">Backout</th>
                    <th className="py-2 px-4 border-b">
                      {displayMode === 'target_candidates' ? 'Revenue (Achieved / Target)' : 'Revenue (Achieved / Target)'}
                    </th>
                    <th className="py-2 px-4 border-b">Achievement Ratio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {sortedData.map((dailyEntry) => {
                    const correspondingRecord = data.find(
                      (record) => record.id === dailyEntry.user
                    );
                    const imageSrc = correspondingRecord
                      ? `${API_BASE_URL}${correspondingRecord.image}`
                      : "";
                    const userName = correspondingRecord
                      ? correspondingRecord.name
                      : "";
                    const targetRev = correspondingRecord
                      ? correspondingRecord.revenue
                      : "";
                    const target = correspondingRecord
                      ? correspondingRecord.target
                      : "";

                    const pipeline =
                      pipelineandturnedup.find(
                        (entry) => entry.user === dailyEntry.user
                      )?.pipline || "";

                    const isExpanded = expandedRows === dailyEntry.user;

                    return (
                      <React.Fragment key={dailyEntry.user}>
                        <tr style={{ fontSize: "14px" }}>
                          <td className="py-2 px-4 border-b text-center">
                            {imageSrc && (
                              <img
                                src={imageSrc}
                                className="w-auto"
                                style={{ height: "100px" }}
                                alt=""
                              />
                            )}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {userName}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "resume_forwarded")}
                          >
                            {dailyEntry.resume_forwarded}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "pipeline")}
                          >
                            {uniquePipelineCounts[dailyEntry.user] || 0} / {pipeline === "" ? "0" : pipeline}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "turned_up")}
                          >
                            {dailyEntry.turned_up}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "selected")}
                          >
                            {dailyEntry.selected} / {target}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "joined")}
                          >
                            {dailyEntry.joined || 0}
                          </td>
                          <td
                            className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                            onClick={() => toggleExpandedRow(dailyEntry.user, "backout")}
                          >
                            {dailyEntry.backout || 0}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {displayMode === 'target_candidates'
                              ? `${isNaN(dailyEntry.revenue) ? '0.00' : parseFloat(dailyEntry.revenue).toFixed(2)} / ${parseFloat(targetRev).toFixed(2)} lac`
                              : `${isNaN(dailyEntry.revenue) ? '0.00' : parseFloat(dailyEntry.revenue).toFixed(2)} / ${parseFloat(targetRev).toFixed(2)} lac`
                            }
                          </td>
                          <td
                            className={`py-2 px-4 border-b text-center ${(() => {
                              const percentage = displayMode === 'target_candidates'
                                ? (target > 0 ? (dailyEntry.selected / target) * 100 : 0)
                                : (targetRev > 0 ? (dailyEntry.revenue / targetRev) * 100 : 0);

                              if (percentage >= 100) return "text-blue-600";
                              if (percentage >= 95) return "text-green-500";
                              if (percentage >= 85) return "text-yellow-500";
                              return "text-red-500";
                            })()
                              }`}
                          >
                            {(() => {
                              const percentage = displayMode === 'target_candidates'
                                ? (target > 0 ? (dailyEntry.selected / target) * 100 : 0)
                                : (targetRev > 0 ? (dailyEntry.revenue / targetRev) * 100 : 0);

                              if (targetRev === 0 && displayMode === 'revenue') {
                                return <span className="text-red-500">0%</span>;
                              }
                              if (target === 0 && displayMode === 'target_candidates') {
                                return <span className="text-red-500">0%</span>;
                              }
                              return `${percentage.toFixed(2)}%`;
                            })()}
                          </td>
                        </tr>

                        {/* Extended Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="9" className="p-4 bg-gray-100">
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead className="bg-gray-300">
                                  <tr>
                                    <th className="py-2 px-4 border-b">Candidate Name</th>
                                    <th className="py-2 px-4 border-b">Hospital</th>
                                    <th className="py-2 px-4 border-b">City</th>
                                    <th className="py-2 px-4 border-b">State</th>
                                    <th className="py-2 px-4 border-b">Speciality</th>
                                    <th className="py-2 px-4 border-b">Status</th>
                                    <th className="py-2 px-4 border-b">Revenue</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {candidateRecords[dailyEntry.user]?.map((record) => {
                                    const isNestedExpanded = nestedExpandedRows[dailyEntry.user] === record.id;
                                    return (
                                      <React.Fragment key={record.id}>
                                        <tr
                                          onClick={() => toggleNestedExpandedRow(dailyEntry.user, record.id)}
                                          style={{
                                            cursor: "pointer",
                                            backgroundColor: isNestedExpanded ? "#f0f8ff" : "transparent",
                                          }}
                                        >
                                          <td className="py-4 px-4 border-b text-center">{record.candidate_name}</td>
                                          <td className="py-4 px-4 border-b text-center">{record.hospital}</td>
                                          <td className="py-4 px-4 border-b text-center">{record.city}</td>
                                          <td className="py-4 px-4 border-b text-center">{record.state}</td>
                                          <td className="py-4 px-4 border-b text-center">{record.speciality}</td>
                                          <td className="py-4 px-4 border-b text-center" style={{
                                            color: record.status === "resume_forwarded" ? "green"
                                              : record.status === "pipeline" ? "green"
                                                : record.status === "turned_up" ? "orange"
                                                  : record.status === "backout" ? "red"
                                                    : record.status === "not selected" ? "gray"
                                                      : record.status === "selected" ? "blue"
                                                        : record.status === "joined" ? "green"
                                                        : "black"
                                          }}>
                                            {record.status}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">{record.revenue}</td>
                                        </tr>
                                        {isNestedExpanded && (
                                          <tr style={{ backgroundColor: "#96DED1", border: "3px solid #f0f8ff" }}>
                                            <td colSpan="7" className="p-3">
                                              <div><strong>Remark:</strong> {record.remark}</div>
                                              <div><strong>Date Entered:</strong> {record.date_entered}</div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Totals Row */}
                  <tr className="bg-gray-300" style={{ fontSize: "17px" }}>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      Total
                    </td>
                    <td className="py-2 px-4 border-b"></td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalForwarded}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalPipeline}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalTurnedUp}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalSelected} / {totalTargetCandidates}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalJoined}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold">
                      {totalBackout}
                    </td>
                    <td className="py-2 px-4 border-b text-center font-bold ">
                      <span className={` ${(totalPercentage) <= 50
                        ? "text-red-500"
                        : (totalPercentage) < 90
                          ? "text-yellow-500"
                          : (totalPercentage) < 115
                            ? "text-green-500"
                            : "text-blue-700"
                        }`}>
                        {totalRevenue.toFixed(2)} {" "}
                      </span>/
                      {totalTargetRevenue.toFixed(2)} lac
                    </td>
                    <td className={`py-2 px-4 border-b text-center font-bold ${(totalPercentage) <= 50
                      ? "text-red-500"
                      : (totalPercentage) < 90
                        ? "text-yellow-500"
                        : (totalPercentage) * 100 < 115
                          ? "text-green-500"
                          : "text-blue-700"
                      }`}>
                      {totalPercentage.toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        </div>
      </section>
      {/* <Announcementslider /> */}
    </>
  );
}

export default PrevMonthRecords;
